import { ReactNode } from 'react';
import styled from 'styled-components';
import { FlexSpacer } from 'ui/flexSpacer';
import { Skeleton } from 'ui/skeleton';
import { H4, spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { AnalyticTile, AnalyticTileProps } from './AnalyticTile';
import { Card } from './BillingPage.styled';

export const CardContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
  padding: ${spacing.plusHalf};
`;

const CardTitle = styled(H4)`
  margin-bottom: 0;
`;

const AnalyticTiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${spacing.x16}, 1fr)); 
  gap: ${spacing.double};
`;

export type AnalyticCardProps = {
  /** card action to display in header such as project breakdown links or download actions */
  CardAction?: ReactNode;
  /** content placed in a tooltip to explain the metric */
  description?: string;
  /** loading state for display */
  isLoading?: boolean;
  /** name of the section */
  title: string;
  /** analytic details shaped by the AnalyticTile component */
  metrics: AnalyticTileProps[];
  /** additional free-form content to be displayed after analytic details */
  children?: ReactNode;
};

export const AnalyticCard = ({
  CardAction,
  children,
  description,
  isLoading,
  metrics = [],
  title,
  ...props
}: AnalyticCardProps) =>
  isLoading ? (
    <CardContainer data-test="analytic-card" {...props}>
      <FlexSpacer alignItems="center" justifyContent="space-between">
        <Skeleton height={32} />
      </FlexSpacer>
      <div>
        <Skeleton height={160} />
      </div>
    </CardContainer>
  ) : (
    <CardContainer data-test="analytic-card" {...props}>
      <FlexSpacer alignItems="center" justifyContent="space-between">
        <CardTitle data-test="analytic-card-title">
          <Tooltip isEnabled={!!description} isUnderlined tooltipContent={description}>
            {title}
          </Tooltip>
        </CardTitle>
        {CardAction}
      </FlexSpacer>
      <AnalyticTiles data-test="analytic-card-tiles">
        {metrics.map((metric) => {
          return <AnalyticTile key={metric.metricName} {...metric} />;
        })}
      </AnalyticTiles>
      {!!children && <div>{children}</div>}
    </CardContainer>
  );
